import { useTranslation } from 'react-i18next';
import { RootState, setUserMetadata, useAppDispatch, useGetUserProfileDetailsQuery, useUpdateUserProfileDetailsMutation, useUpdateUserRoleMutation } from "@circuitry-ai/doc-data";
import { checkType, CtyShowToast, ErrorBoundary } from "@circuitry-ai/doc-ui";
import UserDetailsForm, { UserFormDetails } from "./userDetailsForm";
import { useSelector } from "react-redux";

const UserDetailsFormWithLoadingAndError = ErrorBoundary(UserDetailsForm);

const UserProfile = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const userAuthDetails = useSelector((state: RootState) => state.auth.user);
    const { details: user, isSSOUser } = userAuthDetails;
    const { data: userProfileData, error: userDetailsError, isLoading: userProfileLoading, isFetching: userProfileFetching } = useGetUserProfileDetailsQuery(user?.sub ?? '', { refetchOnMountOrArgChange: true, skip: !user?.sub });
    const [updateUserProfileDetails, { isLoading: isSavingUserDetails }] = useUpdateUserProfileDetailsMutation();
    const [updateUserRole, { isLoading: isUpdatingUserRole }] = useUpdateUserRoleMutation();
    const handleUserRoleUpdate = async (roles: string[]) => {
        try {
            const payLoad = {
                userID: user?.sub,
                roles: roles
            }
            const response = await updateUserRole(payLoad).unwrap();
            CtyShowToast(checkType.success, response?.message);
        } catch (error) {
            CtyShowToast(checkType.error, `${t('Failed to update user role')}: ${(error as Error)?.message || 'Something went wrong!'}`)
        }
    }

    const updateUserDetails = async (userFormDetails: UserFormDetails) => {
        try {
            const payLoad = {
                userData: {
                    ...(!isSSOUser && {
                        name: userFormDetails.firstName,
                        given_name: userFormDetails.firstName,
                        family_name: userFormDetails.lastName,
                        email: userFormDetails.email
                    }),
                    user_metadata: {
                        phone_number: userFormDetails.phoneNumber,
                        skill: userFormDetails.skillSet,
                        language: userFormDetails.defaultLanguage,
                        timezone: userFormDetails.timezone,
                        preference: {
                            dateFormat: userFormDetails.dateFormat,
                            dateTimeFormat: userFormDetails.dateTimeFormat,
                        },
                        ...(userFormDetails.accessLevel != null && { access_level: userFormDetails.accessLevel })
                    }
                },
                userID: user?.sub
            }
            const response = await updateUserProfileDetails({ ...payLoad }).unwrap();
            dispatch(setUserMetadata(payLoad.userData.user_metadata));
            CtyShowToast(checkType.success, response?.message);
        } catch (error) {
            CtyShowToast(checkType.error, `${t('Failed to update profile details')}: ${(error as Error)?.message || 'Something went wrong!'}`)
        }
    }

    const isFetchingUserDetails = userProfileLoading || userProfileFetching;
    const submitButtonText = !isFetchingUserDetails && (userProfileData?.user_metadata && Object.keys(userProfileData?.user_metadata).length > 0) ? 'Update' : 'Save';


    return (
        <div className="flex flex-col h-full">
            <div className="flex flex-col gap-1 my-3">
                <div className="font-poppins text-lg font-semibold leading-7 text-left">{t('Profile')}</div>
                <p className="font-poppins text-sm font-normal leading-5 text-left text-default-500">{t('Manage your personal information and preferences')}</p>
            </div>
            <UserDetailsFormWithLoadingAndError
                error={userDetailsError}
                isLoading={isFetchingUserDetails}
                handleUserRoleUpdate={handleUserRoleUpdate}
                updateUserDetails={updateUserDetails}
                userProfileData={userProfileData}
                submitButtonText={submitButtonText}
                isUpdatingUserRole={isUpdatingUserRole}
                isSavingUserDetails={isSavingUserDetails}
                viewUserRole={false}
                isSSOUser={Boolean(isSSOUser)}
            />
        </div>
    )
}

export default UserProfile;

